import axios from 'axios'
import { cerrarSesion } from '../../helpers/cerrarSesion'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import * as loginService from './loginService'

const ID_ERROR_UNAUTHORIZED = 401
const ID_ERROR_NOT_FOUND = 404

let getToken = () => localStorage.getItem('jwt')
let onTokenRenovado = null
let onSesionExpirada = null

export const setTokenGetter = (tokenGetter) => {
  getToken = tokenGetter
}

export const setOnTokenRenovado = (callback) => {
  onTokenRenovado = callback
}

export const setOnSesionExpirada = (callback) => {
  onSesionExpirada = callback
}

let renovandoToken = false
let requestPendientes = []

export const renovarToken = () => {
  if (renovandoToken) {
    return new Promise((resolve, reject) => {
      requestPendientes.push({ resolve, reject })
    })
  }

  renovandoToken = true

  return loginService
    .requestToken(
      {
        usuario: localStorage.getItem('usuario'),
        empid: localStorage.getItem('empid'),
        refreshToken: localStorage.getItem('refreshToken')
      },
      { _retry: true }
    )
    .then(({ data }) => {
      if (!data?.token) {
        throw new Error('Renovación de token sin token válido')
      }

      localStorage.setItem('jwt', data.token)
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken)
      }
      setTokenGetter(() => data.token)
      onTokenRenovado?.(data)

      requestPendientes.forEach(({ resolve }) => resolve(data.token))
      requestPendientes = []
      return data.token
    })
    .catch((renovacionError) => {
      requestPendientes.forEach(({ reject }) => reject(renovacionError))
      requestPendientes = []
      cerrarSesion(onSesionExpirada ?? (() => localStorage.clear()))
      throw renovacionError
    })
    .finally(() => {
      renovandoToken = false
    })
}

axios.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.authorization = `Bearer ${token}`
    }
    if (
      !config.headers['Content-Type'] &&
      !config.headers['content-type'] &&
      !(config.data instanceof FormData)
    ) {
      config.headers['Content-Type'] = 'application/json'
    }
    return config
  },
  (error) => Promise.reject(error)
)

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    const dataNoEncontrada =
      error.config?.meta?.devolverNull && error.response?.status === ID_ERROR_NOT_FOUND

    if (error.config?.meta?.toastMessage && !dataNoEncontrada) {
      const tipoError = error.config.meta.tipoError || 'general'
      toastErrorLog(error, error.config.meta.toastMessage, tipoError)
    }

    if (!originalRequest) {
      return dataNoEncontrada ? null : Promise.reject(error)
    }

    const esTokenExpirado =
      error.response?.status === ID_ERROR_UNAUTHORIZED &&
      localStorage.getItem('refreshToken') &&
      !originalRequest._retry

    if (!esTokenExpirado) {
      return dataNoEncontrada ? null : Promise.reject(error)
    }

    originalRequest._retry = true

    const tokenError = originalRequest.headers.authorization?.split(' ')[1]
    const tokenActual = localStorage.getItem('jwt')
    if (tokenActual && tokenActual !== tokenError) {
      originalRequest.headers.authorization = `Bearer ${tokenActual}`
      return axios(originalRequest)
    }

    try {
      const nuevoToken = await renovarToken()
      originalRequest.headers.authorization = `Bearer ${nuevoToken}`
      return await axios(originalRequest)
    } catch (renovacionError) {
      return Promise.reject(renovacionError)
    }
  }
)

export default {
  get: axios.get,
  request: axios.request,
  post: axios.post,
  put: axios.put,
  patch: axios.patch,
  delete: axios.delete
}
